import { DownOutlined } from '@ant-design/icons';
import {
  DrawerForm,
  ModalForm,
  PageContainer,
  ProFormDigit,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Dropdown, Flex, Input, List, Segmented, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import TopicLensPreview from '../components/TopicLensPreview';
import {
  getIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
  updateIpCreatorRecord,
} from '../services/ip-creator';

const { Search } = Input;

const resource = 'topic-lens';
const resourceName = '选题策划';

const statusOptions = ['已淘汰', '已入选'].map((value) => ({
  label: value,
  value,
}));

const statusFilterOptions = [{ label: '全部', value: 'all' }, ...statusOptions];
const recommendedArcTypeOptions = [
  { label: '文章', value: 'articleArc' },
  { label: '脚本', value: 'scriptArc' },
];

export const renderTopicTitle = (record) => (
  <Typography.Text ellipsis={{ tooltip: record.title }}>{record.title || '-'}</Typography.Text>
);

const ListContent = ({ data }) => (
  <Space size="large" wrap>
    <Space vertical size="small">
      <Typography.Text type="secondary">状态</Typography.Text>
      <Typography.Text>{data.status || '-'}</Typography.Text>
    </Space>
    <Space vertical size="small">
      <Typography.Text type="secondary">评分</Typography.Text>
      <Typography.Text>{data.score ?? '-'}</Typography.Text>
    </Space>
    <Space vertical size="small">
      <Typography.Text type="secondary">创作者</Typography.Text>
      <Typography.Text>{data.userName || '-'}</Typography.Text>
    </Space>
    <Space vertical size="small">
      <Typography.Text type="secondary">创建时间</Typography.Text>
      <Typography.Text>
        {data.createdAt ? dayjs(data.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
      </Typography.Text>
    </Space>
  </Space>
);

ListContent.propTypes = {
  data: PropTypes.shape({
    createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    score: PropTypes.number,
    status: PropTypes.string,
    userName: PropTypes.string,
  }),
};

const TopicLens = () => {
  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [query, setQuery, queryString] = useQueryParams();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationPath();
  const { message, modal } = App.useApp();
  const status = Array.isArray(query.status) ? query.status.at(-1) : query.status || 'all';
  const title = Array.isArray(query.title) ? query.title.at(-1) : query.title || '';

  const reload = () => queryClient.invalidateQueries({ queryKey: ['ip-creator', resource] });

  const { data: listResult, isLoading: loading } = useQuery({
    queryKey: ['ip-creator', resource, organizationId, queryString],
    queryFn: () =>
      queryIpCreatorRecords(resource, {
        organizationId,
        title: title || undefined,
        status: status === 'all' ? undefined : status,
        current: 1,
        pageSize: 50,
      }),
  });

  const list = listResult?.data || [];

  const { mutateAsync: updateRecord, isPending: updating } = useMutation({
    mutationFn: (payload) => updateIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已更新`);
      reload();
    },
    onError: () => message.error(`${resourceName}更新失败，请重试`),
  });

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: (payload) => getIpCreatorRecord(resource, payload),
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const updateSearchParam = (key, value) => {
    setQuery({ [key]: value || undefined });
  };

  const removeRecordWithConfirm = (record) => {
    modal.confirm({
      title: `移除${resourceName}`,
      content: `确认移除 ${record.title || record.id}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => removeRecord({ data: { id: record.id, organizationId } }),
    });
  };

  const handleMoreAction = (key, record) => {
    if (key === 'edit') {
      setEditRecord(record);
      return;
    }
    if (key === 'remove') {
      removeRecordWithConfirm(record);
    }
  };

  const renderFormFields = ({ lockSeedSource = false } = {}) => (
    <>
      {lockSeedSource ? (
        <ProFormText name="seedTitle" label="种子标题" disabled />
      ) : (
        <ProFormText
          name="seedId"
          label="种子ID"
          rules={[{ required: true, message: '请输入种子ID' }]}
        />
      )}
      <ProFormSelect
        name="seedType"
        label="种子类型"
        disabled={lockSeedSource}
        options={[
          { label: '热点采集', value: 'hotPot' },
          { label: '创作规划', value: 'seedPlan' },
        ]}
        rules={[{ required: true, message: '请选择种子类型' }]}
      />
      <ProFormText
        name="title"
        label="选题标题"
        rules={[{ required: true, message: '请输入选题标题' }]}
      />
      <ProFormText
        name="audience"
        label="目标受众"
        rules={[{ required: true, message: '请输入目标受众' }]}
      />
      <ProFormTextArea
        name="coreQuestion"
        label="核心问题"
        rules={[{ required: true, message: '请输入核心问题' }]}
      />
      <ProFormText
        name="tension"
        label="内容张力"
        rules={[{ required: true, message: '请输入内容张力' }]}
      />
      <ProFormTextArea
        name="thesis"
        label="核心判断"
        rules={[{ required: true, message: '请输入核心判断' }]}
      />
      <ProFormText
        name="value"
        label="核心价值"
        rules={[{ required: true, message: '请输入核心价值' }]}
      />
      <ProFormText
        name="angle"
        label="选题切角"
        rules={[{ required: true, message: '请输入选题切角' }]}
      />
      <ProFormTextArea
        name="keyPoints"
        label="关键要点"
        rules={[{ required: true, message: '请输入关键要点' }]}
      />
      <ProFormTextArea
        name="methodPath"
        label="方法路径"
        rules={[{ required: true, message: '请输入方法路径' }]}
      />
      <ProFormTextArea
        name="habitPath"
        label="组织路径"
        rules={[{ required: true, message: '请输入组织路径' }]}
      />
      <ProFormTextArea
        name="closingIntent"
        label="收束意图"
        rules={[{ required: true, message: '请输入收束意图' }]}
      />
      <ProFormSelect
        name="recommendedArcType"
        label="推荐形态"
        options={recommendedArcTypeOptions}
        rules={[{ required: true, message: '请选择推荐形态' }]}
      />
      <ProFormTextArea
        name="recommendedArcReason"
        label="推荐原因"
        rules={[{ required: true, message: '请输入推荐原因' }]}
      />
      <ProFormSelect
        name="status"
        label="状态"
        options={statusOptions}
        rules={[{ required: true, message: '请选择状态' }]}
      />
      <ProFormTextArea
        name="reason"
        label="结果原因"
        rules={[{ required: true, message: '请输入结果原因' }]}
      />
      <ProFormDigit
        name="score"
        label="策划评分"
        rules={[{ required: true, message: '请输入策划评分' }]}
      />
    </>
  );

  return (
    <PageContainer content="基于创作规划、热点线索、搜集材料和 IP 创作内核进行结构化选题">
      <Flex vertical gap="large">
        <Card variant="borderless" title={resourceName}>
          <Flex gap="middle" wrap>
            <Segmented
              value={status}
              onChange={(value) => updateSearchParam('status', value)}
              options={statusFilterOptions}
            />
            <Search
              allowClear
              defaultValue={title}
              key={title}
              placeholder="选题标题"
              onSearch={(value) => updateSearchParam('title', value)}
              variant="filled"
            />
          </Flex>
          <List
            size="large"
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, showQuickJumper: true, pageSize: 10 }}
            dataSource={list}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key="detail"
                    type="link"
                    onClick={() => getRecord({ data: { id: item.id, organizationId } })}
                  >
                    详情
                  </Button>,
                  <Dropdown
                    key="more"
                    menu={{
                      onClick: ({ key }) => handleMoreAction(key, item),
                      items: [
                        {
                          key: 'edit',
                          label: '编辑',
                        },
                        {
                          key: 'remove',
                          danger: true,
                          disabled: removing,
                          label: '移除',
                        },
                      ],
                    }}
                  >
                    <Button type="link">
                      更多 <DownOutlined />
                    </Button>
                  </Dropdown>,
                ]}
              >
                <List.Item.Meta title={renderTopicTitle(item)} description={item.reason || '-'} />
                <ListContent data={item} />
              </List.Item>
            )}
          />
        </Card>
      </Flex>
      <ModalForm
        key={editRecord?.id || 'edit'}
        title={`编辑${resourceName}`}
        open={Boolean(editRecord)}
        width={560}
        initialValues={
          editRecord ? { ...editRecord, seedTitle: editRecord.seedTitle || editRecord.seedId } : {}
        }
        modalProps={{
          destroyOnHidden: true,
          okButtonProps: { loading: updating },
        }}
        onOpenChange={(open) => {
          if (!open) {
            setEditRecord(null);
          }
        }}
        onFinish={async (values) => {
          try {
            await updateRecord({
              data: {
                id: editRecord.id,
                organizationId,
                ...values,
                seedId: editRecord.seedId,
                seedType: editRecord.seedType,
              },
            });
            setEditRecord(null);
            return true;
          } catch {
            return false;
          }
        }}
      >
        {renderFormFields({ lockSeedSource: true })}
      </ModalForm>
      <DrawerForm
        title={`${resourceName}详情`}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRecord(null);
          }
        }}
        submitter={false}
        width={1024}
      >
        <TopicLensPreview record={detailRecord} />
      </DrawerForm>
    </PageContainer>
  );
};

export default TopicLens;
